import React from 'react';
import { useTheme } from '@veneer/theme';
import { TextBoxInterface } from './types';
import propTypes from './prop_types';
import useStyles from './use_style';
import isVeneerIcon from '../utils/is_veneer_icon';
import HelperText from '../shared/form/helper_text';
import Label from '../shared/form/label';
import { getTrailingIcon } from './get_trailing_icon';
import useInputStyles from '../shared/form/use_input_styles';
import mergeRefs from 'react-merge-refs';
import useInputId from '../shared/form/use_input_id';
import { useLeadingWidth, useTrailingWidth } from '../shared/form/use_width';
import { IconMinus, IconPlus } from '../icons';
import { UtilityButtonDiv } from '../shared/utility_button';

const TextBox = React.forwardRef<
  HTMLInputElement,
  TextBoxInterface &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof TextBoxInterface>
>(
  (
    {
      className,
      disabled = false,
      disableLabelShrinking = false,
      error = false,
      customStyle,
      helperText,
      helperTextState = 'default',
      helperTextVisibility = 'auto',
      id: idProp,
      inputComponent: Component = 'input',
      label,
      noTruncate = false,
      onChange,
      placeholder,
      required = false,
      separateLabel: separateLabelProp = false,
      trailingArea,
      trailingButton,
      trailingIcon,
      type = 'text',
      parentRef,
      maxLength,
      prefix,
      suffix,
      counter,
      countCallback,
      ...inputProps
    },
    ref
  ) => {
    const theme = useTheme();
    const isPill = theme.shape === 'pill';
    const isHc = theme.mode === 'contrast';
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { id, helperTextId, labelId, prefixId, suffixId } = useInputId(
      'vn-textbox',
      idProp
    );

    const separateLabel =
      theme.density === 'high' || prefix || suffix ? true : separateLabelProp;

    const { leadingRef, leadingWidth } = useLeadingWidth([prefix, suffix]);
    const { trailingRef, trailingWidth } = useTrailingWidth([prefix, suffix]);

    const icon = getTrailingIcon(type, trailingIcon);
    const handleIconClick = React.useCallback(() => {
      inputRef?.current?.focus();
    }, []);

    const handleChange = React.useCallback<
      React.ChangeEventHandler<HTMLInputElement>
    >(
      (event) => {
        onChange?.(event.target.value, event);
      },
      [onChange]
    );

    const style = useStyles();
    const containerStyle = useInputStyles({
      leadingWidth,
      trailingWidth,
    });

    const spinner = React.useMemo(() => {
      return type === 'number' ? (
        <div css={style.spinner} aria-hidden>
          <UtilityButtonDiv
            icon={<IconMinus />}
            disabled={disabled || inputProps.readOnly}
            /* eslint-disable-next-line react/jsx-no-bind */
            onClick={() => inputRef.current?.stepDown()}
          />
          <div css={style.spinnerDivider} />
          <UtilityButtonDiv
            icon={<IconPlus />}
            disabled={disabled || inputProps.readOnly}
            /* eslint-disable-next-line react/jsx-no-bind */
            onClick={() => inputRef.current?.stepUp()}
          />
        </div>
      ) : undefined;
    }, [type, style, disabled, inputProps.readOnly]);

    return (
      <div
        css={[
          containerStyle.root.base,
          helperTextVisibility === 'auto' &&
            containerStyle.root.helperTextVisible,
          customStyle,
        ]}
        ref={parentRef}
      >
        {label && separateLabel && (
          <div css={style.label}>
            <Label
              htmlFor={id}
              disabled={disabled}
              id={labelId}
              required={required ?? false}
              truncate={!noTruncate}
              counter={counter}
              counterLimit={maxLength}
              countCallback={countCallback}
              inputRef={inputRef}
            >
              {label}
            </Label>
          </div>
        )}

        <div css={[style.container]} className={className}>
          {label && !separateLabel && (
            <Label
              htmlFor={id}
              applyTo="input"
              disabled={disabled}
              disableLabelShrinking={disableLabelShrinking}
              id={labelId}
              inputRef={inputRef}
              inside
              required={required ?? false}
              counter={counter}
              counterLimit={maxLength}
              countCallback={countCallback}
              leadingWidth={leadingWidth}
              trailingWidth={trailingWidth}
            >
              {label}
            </Label>
          )}
          <Component
            aria-describedby={[
              helperText && helperTextId,
              prefix && prefixId,
              suffix && suffixId,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-invalid={error}
            aria-required={required}
            css={[
              style.input,
              containerStyle.input.base,

              containerStyle.input.paddings.base,
              separateLabel && containerStyle.input.paddings.separateLabel,
              isPill && containerStyle.input.paddings.pill,

              isHc && containerStyle.input.hc,
            ]}
            disabled={disabled}
            id={id}
            placeholder={placeholder ?? ' '}
            ref={mergeRefs([inputRef, ref])}
            type={type}
            onChange={handleChange}
            maxLength={maxLength}
            {...inputProps}
          />
          {prefix && (
            <div
              id={prefixId}
              css={[
                containerStyle.input.prefix.base,
                isPill && containerStyle.input.prefix.pill,
              ]}
              className="label-regular"
              ref={leadingRef}
            >
              {prefix}
            </div>
          )}
          {(trailingArea || trailingButton || icon || suffix || spinner) && (
            <div
              className="vn-input__trailing-area"
              css={[
                containerStyle.input.trailingArea.base,
                isPill && containerStyle.input.trailingArea.pill,
              ]}
              ref={trailingRef}
            >
              {suffix && (
                <div
                  id={suffixId}
                  className="label-regular"
                  css={containerStyle.input.suffix}
                >
                  {suffix}
                </div>
              )}
              {trailingArea}
              {trailingButton &&
                React.isValidElement(trailingButton) &&
                // https://github.com/microsoft/TypeScript/issues/52232
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                React.cloneElement<any>(trailingButton, {
                  // compat with old version
                  className: 'vn-trailing_button',
                })}
              {spinner}
              {icon && isVeneerIcon(icon) && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                  onClick={handleIconClick}
                  css={containerStyle.input.icon.container}
                >
                  {React.cloneElement(icon, {
                    customStyle: [
                      containerStyle.input.icon.base,
                      disabled && containerStyle.input.icon.disabled,
                    ],
                    // compat with old version
                    className: 'vn-input__icon',
                    disabled,
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {helperText && (
          <HelperText
            disabled={disabled}
            error={error}
            helperTextState={helperTextState}
            helperTextVisibility={helperTextVisibility}
            id={helperTextId}
          >
            {helperText}
          </HelperText>
        )}
      </div>
    );
  }
);

TextBox.propTypes = propTypes;

TextBox.displayName = 'TextBox';

export default TextBox;
